import React from 'react';

import { useGoogleLogin as useGoogleAuthLogin } from '@react-oauth/google';
import { useMutation } from '@tanstack/react-query';

import { apiClient, snackbar } from '@/_services';
import { log } from '@/_shared';

import { LOGIN_API_ERROR } from '../constants';
import type { LoginApiErrorResponse, LoginHandlerParams } from '../types';

import type { GoogleLoginApiPayload, GoogleLoginApiResponse } from './types';

export const useGoogleLogin = ({ onSuccess, onError }: LoginHandlerParams) => {
  const [Error, setError] = React.useState<React.ReactNode>(null);

  const { mutateAsync: googleLogin, isPending: isLoading } = useMutation<
    GoogleLoginApiResponse,
    LoginApiErrorResponse,
    GoogleLoginApiPayload
  >({
    mutationFn: (payload) =>
      apiClient.post({
        endpoint: '/google/login/',
        payload,
        isAuth: false,
      }),
  });

  const handleGoogleLoginSuccess = (data: GoogleLoginApiResponse, variables: Record<string, string>) => {
    if (!data) {
      return;
    }
    if (data.detail === LOGIN_API_ERROR.INSTRUCTOR_ACCOUNT_EXISTS.code) {
      setError(
        LOGIN_API_ERROR.INSTRUCTOR_ACCOUNT_EXISTS.ErrorNode({
          onReset: () => setError(null),
        })
      );
    } else {
      onSuccess({ access: data.access_token, refresh: data.refresh_token }, variables);
    }
  };

  const initGoogleLogin = useGoogleAuthLogin({
    flow: 'implicit',
    onSuccess: async (data) => {
      try {
        await googleLogin({ token: data.access_token }, { onSuccess: handleGoogleLoginSuccess, onError });
      } catch (error) {
        console.error('Unable to login with google', error);
      }
    },
    onError: (errorResponse) => {
      log('Error logging in gmail', errorResponse);
      snackbar.error("Couldn't sign in.");
    },
  });

  return { initGoogleLogin, isLoading, ErrorNode: Error };
};
